<template>
  <div class="toolbox-container">
    <div class="mb-2">
      <analyze-search v-model="search" :hide-details="true" />
      <!-- <div class="mt-3">
        <b data-testid="table-name-column"> {{ contextLabel }} </b>
      </div> -->
    </div>
    <draggable
      v-model="filteredTools"
      class="mb-2"
      item-key="order"
      :component-data="{ tag: 'ul', name: 'flip-list', type: 'transition' }"
      v-bind="dragOptions"
      @start="isDragging = true"
      @end="isDragging = false"
    >
      <template #item="{ element }">
        <li
          class="list-group-item"
          @dragstart="dragStart(element, $event)"
          @click="selectTool(element)"
        >
          <i
            :class="element.fixed ? 'fa fa-anchor' : 'glyphicon glyphicon-pushpin'"
            aria-hidden="true"
            @click="element.fixed = !element.fixed"
          ></i>
          <span class="dot" :style="{ backgroundColor: stringToColor(element.label) }"></span>
          <span data-testid="table-name-column">{{ element.label }}</span>
        </li>
      </template>
    </draggable>
    <div
      v-if="
        toolbox.tools &&
        toolbox.toolsCount &&
        toolbox.tools.length < toolbox.toolsCount &&
        !props.targetUuid
      "
    >
      And {{ toolbox.toolsCount }} more tools available by search
    </div>

    <v-btn
      aria-label="Toggle Toolbox Position"
      class="secondary-btn flex-grow-0"
      block
      :elevation="0"
      prepend-icon="mdi-swap-horizontal"
      @click="handleToggleLayout"
    >
      {{ labels.CUSTOM_METRIC.TOGGLE_TOOLBOX_POSITION }}
    </v-btn>
  </div>
</template>

<script lang="ts" setup>
  import { computed, ref, watch } from 'vue'
  import AnalyzeSearch from '@/components/Inputs/AnalyzeSearch.vue'
  import draggable from 'vuedraggable'
  import labels from '@/translations/labels.json'
  import { Tool, Toolbox } from '@/models/custom-metric.model'

  const isDragging = ref<boolean>(false)

  const search = ref<string>('')

  const props = defineProps<{
    toolbox: Toolbox
    targetUuid?: string
  }>()

  const emit = defineEmits<{
    selectTool: [value: Tool]
    dragStart: [value: Tool]
    searchUpdate: [value: string]
    onToggleButtonClick: []
  }>()

  const filteredTools = computed(() => {
    let result: Tool[] = props.toolbox.tools || []

    result = result.filter((tool) => tool.kind != 'delete')
    return result
  })

  const dragOptions = computed(
    (): { animation: number; group: string; disabled: boolean; ghostClass: string } => {
      return {
        animation: 0,
        group: 'description',
        disabled: false,
        ghostClass: 'ghost',
      }
    },
  )

  watch(search, () => emit('searchUpdate', search.value))
  function selectTool(tool: Tool) {
    emit('selectTool', tool)
  }

  function handleToggleLayout() {
    emit('onToggleButtonClick')
  }

  function dragStart(item: Tool, event: any) {
    emit('dragStart', item)
    const dragData = { item: JSON.stringify(item), type: item.type }
    event.dataTransfer.setData('application/json', JSON.stringify(dragData))
  }

  const stringToColor = (str: string) => {
    // special cases
    switch (str) {
      case 'All Artifacts':
        return '#14213d'
      case 'Traces':
        return '#fca311'
    }

    let hash = 0
    str.split('').forEach((char) => {
      hash = char.charCodeAt(0) + ((hash << 5) - hash)
    })
    let colour = '#'
    for (let i = 0; i < 3; i++) {
      const value = (hash >> (i * 8)) & 0xff
      colour += value.toString(16).padStart(2, '0')
    }
    return colour
  }
</script>

<style scoped>
  .analyze-search-input {
    background-color: white;
  }

  .flip-list-move {
    transition: transform 0.5s;
  }

  .ghost {
    opacity: 0.5;
    background: #c8ebfb;
  }

  .list-group-item {
    cursor: move;
    padding: 10px;
    border: 1px solid #ddd;
    margin-bottom: 5px;
    display: flex;
    align-items: center;
    justify-content: flex-start; /* Align items to the start */
  }

  .list-group-item i {
    cursor: pointer;
    margin-right: 10px;
  }

  .dragging {
    opacity: 0.7;
    background-color: #f9f9f9;
    border-color: #ccc;
  }

  .list-group-item span {
    flex-grow: 1;
    font-size: 1.7ch;
    text-align: left;
  }

  .dot {
    width: 10px;
    height: 10px;
    min-width: 10px;
    min-height: 10px;
    max-width: 10px;
    max-height: 10px;
    border-radius: 50%;
    display: inline-block;
    margin-right: 10px;
  }

  .toolbox-container {
    width: 300px;
    flex-shrink: 0;
    max-height: 80vh;
    overflow-y: auto;
  }
</style>
